/**
 * WAYP-820 — Routing Row Fields configuration matrix.
 *
 * Single source of truth for the 17 row IDs, the 18 column IDs, and the
 * default `configJson` skeleton stored under `OrganizationFeature.configJson`
 * for the five `mod_*_cfrw` feature keys (Dispatch / USPS / Intermodal /
 * FTL Brokerage / Livestock).
 *
 * Imported by:
 *   - packages/server/prisma/seed.ts          (preferencesSchema description)
 *   - packages/server/tests/integration/...   (round-trip + tenant tests)
 *   - packages/client/src/.../RoutingRowFieldsMatrix.tsx
 *   - packages/client/tests/.../routing-row-fields-matrix.test.tsx
 */
export const RRF_MODULE_KEYS = [
    'dispatch',
    'usps',
    'intermodal',
    'ftlbrokerage',
    'livestock',
];
/** Map from module key → enterprise feature key that stores its configJson. */
export const RRF_FEATURE_KEY_BY_MODULE = {
    dispatch: 'mod_dispatch_freight_cfrw',
    usps: 'mod_usps_cfrw',
    intermodal: 'mod_intermodal_cfrw',
    ftlbrokerage: 'mod_ftl_brokerage_cfrw',
    livestock: 'mod_livestock_cfrw',
};
/** Inverse lookup — feature key → module key. */
export const RRF_MODULE_BY_FEATURE_KEY = Object.fromEntries(Object.entries(RRF_FEATURE_KEY_BY_MODULE).map(([m, k]) => [k, m]));
/** All 5 feature keys as a flat array — convenient for tests + validators. */
export const RRF_FEATURE_KEYS = Object.values(RRF_FEATURE_KEY_BY_MODULE);
/**
 * Default Type options — V1 shape: empty / Text / Number / Dropdown / Date / Datetime.
 * Empty string preserves V1's "not set" sentinel.
 *
 * Declared up here (above RRF_ROW_META) so the row-meta entries can pin
 * `lockedDefaultType` to a value from this enum without a forward-reference.
 */
export const RRF_DEFAULT_TYPE_OPTIONS = ['', 'Text', 'Number', 'Dropdown', 'Date', 'Datetime'];
export const RRF_UNITS_OPTIONS_V1 = [
    { value: 'Pieces', label: 'Pieces' },
    { value: 'Tonnes', label: 'Tonnes' },
    { value: 'Metric Tonnes', label: 'Metric Tonnes' },
    { value: 'Cubic Meters', label: 'Cubic Meters' },
    { value: 'Pounds', label: 'Pounds' },
    { value: 'Kilograms', label: 'Kilograms' },
    { value: 'Boxes', label: 'Boxes' },
    { value: 'Pallets', label: 'Pallets' },
    { value: 'Full Load', label: 'Full Load' },
    { value: 'Super B', label: 'Super B' },
    { value: 'Vehicle', label: 'Vehicle' },
    { value: 'Equipment', label: 'Equipment' },
    { value: 'Equipment Load Bed', label: 'Equipment Load Bed' },
    { value: 'Skids', label: 'Skids' },
    { value: 'Tank', label: 'Tank' },
    { value: 'Feet', label: 'Feet' },
    { value: 'Totes', label: 'Totes' },
    { value: 'Short Tons', label: 'Short Tons' },
    { value: 'Gallons', label: 'Gallons' },
    { value: 'Units', label: 'Units' },
    { value: 'Bags', label: 'Bags' },
    { value: 'Packages', label: 'Packages' },
    { value: 'Bundles', label: 'Bundles' },
    { value: 'Crates', label: 'Crates' },
    { value: 'Cartons', label: 'Cartons' },
    { value: 'Container', label: 'Container' },
];
const RRF_PACKAGING_UNITS_ACI_US_TO_CA = [
    ['PLL', 'Air Pallet'],
    ['AMM', 'Ammo Pack'],
    ['BAG', 'Bag'],
    ['BAL', 'Bale'],
    ['BDG', 'Banding'],
    ['BRG', 'Barge'],
    ['BBL', 'Barrel'],
    ['BSK', 'Basket or hamper'],
    ['BEM', 'Beam'],
    ['BLT', 'Belting'],
    ['BIN', 'Bin'],
    ['BIC', 'Bing Chest'],
    ['BOB', 'Bobbin'],
    ['BOT', 'Bottle'],
    ['BOX', 'Box'],
    ['BXI', 'Box, with inner container'],
    ['BRC', 'Bracing'],
    ['BXT', 'Bucket'],
    ['BKG', 'Bulk Bag'],
    ['BDL', 'Bundle'],
    ['CAB', 'Cabinet'],
    ['CAG', 'Cage'],
    ['CAN', 'Can'],
    ['CCS', 'Can Case'],
    ['CBY', 'Carboy'],
    ['CLD', 'Car Load, Rail'],
    ['CAR', 'Carrier'],
    ['CTN', 'Carton'],
    ['CAS', 'Case'],
    ['CSK', 'Cask'],
    ['CHE', 'Cheeses'],
    ['CHS', 'Chest'],
    ['COL', 'Coil'],
    ['CON', 'Cones'],
    ['CNX', 'Connex'],
    ['CNT', 'Container'],
    ['CND', 'Container, Engine'],
    ['CNA', 'Container, Household Goods, Wood'],
    ['CNB', 'Container, Military Airlift'],
    ['CNF', 'Container, Multiwall on Warehouse Pallet'],
    ['CNC', 'Container, Navy Cargo'],
    ['CBC', 'Containers of Bulk Cargo'],
    ['COR', 'Core'],
    ['CRF', 'Corner Reinforcement'],
    ['CRD', 'Cradle'],
    ['CRT', 'Crate'],
    ['CUB', 'Cube'],
    ['CYL', 'Cylinder'],
    ['DRK', 'Double-length Rack'],
    ['DSK', 'Double-length Skid'],
    ['DTB', 'Double-length Tote Bin'],
    ['DRM', 'Drum'],
    ['DBK', 'Dry Bulk'],
    ['DUF', 'Duffle Bag'],
    ['EPR', 'Edge Protection'],
    ['EGG', 'Egg Crating'],
    ['ENV', 'Envelopes'],
    ['FIR', 'Firkin'],
    ['FSK', 'Flask'],
    ['FLO', 'Flo-Bin'],
    ['FWR', 'Forward Reel'],
    ['FRM', 'Frame'],
    ['GOH', 'Garments on Hangers'],
    ['HRK', 'Half-Standard Rack'],
    ['HTB', 'Half-Standard Tote Bin'],
    ['HPR', 'Hamper'],
    ['HED', 'Heads of Beef'],
    ['HGH', 'Hogshead'],
    ['HPT', 'Hopper Truck'],
    ['TLD', 'Intermodal Trailer/Container Load (Rail)'],
    ['JAR', 'Jar'],
    ['JUG', 'Jug'],
    ['KEG', 'Keg'],
    ['KIT', 'Kit'],
    ['KRK', 'Knockdown Rack'],
    ['KTB', 'Knockdown Tote Bin'],
    ['LIF', 'Lifts'],
    ['LVN', 'Lift Van'],
    ['SBC', 'Liner Bag Dry'],
    ['FLX', 'Liner Bag Liquid'],
    ['LNR', 'Liners'],
    ['LID', 'Lip/Top'],
    ['LBK', 'Liquid Bulk'],
    ['LOG', 'Log'],
    ['LSE', 'Loose'],
    ['LUG', 'Lug'],
    ['MSV', 'Military Sealift Command Van'],
    ['MLV', 'Military Van'],
    ['MIX', 'Mixed Container Types'],
    ['MXD', 'Mixed Type Pack'],
    ['MRP', 'Multi-Roll Pack'],
    ['NOL', 'Noil'],
    ['HRB', 'On Hanger or Rack in Boxes'],
    ['WHE', 'On Own Wheel'],
    ['OVW', 'Overwrap'],
    ['PKG', 'Package'],
    ['PCK', 'Packed - not otherwise specified'],
    ['PAL', 'Pail'],
    ['PLT', 'Pallet'],
    ['PAT', 'Pallet - 2 Way'],
    ['PAF', 'Pallet - 4 Way'],
    ['PRT', 'Partitioning'],
    ['PCE', 'Piece'],
    ['PCS', 'Pieces'],
    ['PIR', 'Pims'],
    ['PLN', 'Pipeline'],
    ['PRK', 'Pipe Rack'],
    ['PLF', 'Platform'],
    ['PLC', 'Primary Lift Container'],
    ['POV', 'Private Vehicle'],
    ['QTR', 'Quarter of Beef'],
    ['RCK', 'Rack'],
    ['RAL', 'Rail (Semiconductor)'],
    ['REL', 'Reel'],
    ['RFT', 'Reinforcement'],
    ['RVR', 'Reverse Reel'],
    ['ROL', 'Roll'],
    ['SAK', 'Sack'],
    ['SVN', 'Sea Van'],
    ['SPR', 'Separator/Divider'],
    ['SHT', 'Sheet'],
    ['SHK', 'Shook'],
    ['SHW', 'Shrink Wrapped'],
    ['SID', 'Side of Beef'],
    ['SKD', 'Skid'],
    ['SKE', 'Skid, elevating or lift truck'],
    ['SLV', 'Sleeve'],
    ['SLP', 'Slip Sheet'],
    ['SPI', 'Spin Cylinders'],
    ['SPL', 'Spool'],
    ['SCS', 'Suitcase'],
    ['TNK', 'Tank'],
    ['TKR', 'Tank Car'],
    ['TKT', 'Tank Truck'],
    ['TRC', 'Tierce'],
    ['TBN', 'Tote Bin'],
    ['TTC', 'Tote Can'],
    ['TRY', 'Tray'],
    ['TRI', 'Triwall Box'],
    ['TRU', 'Truck'],
    ['TRK', 'Trunk and Chest'],
    ['TSS', 'Trunk, Salesmen Sample'],
    ['TUB', 'Tub'],
    ['TBE', 'Tube'],
    ['UNT', 'Unit'],
    ['UNP', 'Unpacked'],
    ['VPK', 'Van Pack'],
    ['VEH', 'Vehicles'],
    ['WLC', 'Wheeled Carrier'],
    ['WRP', 'Wrapped'],
].map(([value, label]) => ({
    value: value,
    label: label,
    categoryLabel: 'US Units - (ACI: US to CA)',
}));
const RRF_PACKAGING_UNITS_ACE_CA_TO_US = [
    ['BAG', 'Bag'],
    ['BLE', 'Bale'],
    ['BBL', 'Barrel'],
    ['BSK', 'Basket'],
    ['BIN', 'Bin'],
    ['BIC', 'Bing Chest'],
    ['BOX', 'Box'],
    ['BKT', 'Bucket'],
    ['BDL', 'Bundle'],
    ['CAN', 'Can'],
    ['CCS', 'Can Case'],
    ['CBY', 'Carboy'],
    ['CAR', 'Carcass'],
    ['CTN', 'Carton'],
    ['CAS', 'Case'],
    ['CSK', 'Cask'],
    ['CHS', 'Chest'],
    ['COL', 'Coil'],
    ['CBC', 'Container Bulk Cargo'],
    ['COR', 'Cord'],
    ['CRT', 'Crate'],
    ['CYL', 'Cylinder'],
    ['DRM', 'Drum'],
    ['DBK', 'Dry Bulk'],
    ['GAL', 'Gallon'],
    ['HMP', 'Hamper'],
    ['HED', 'Heads of Beef'],
    ['KEG', 'Keg'],
    ['LVN', 'Lift Van'],
    ['LBK', 'Liquid Bulk'],
    ['LOG', 'Logs'],
    ['LUG', 'Lugs'],
    ['PKG', 'Package'],
    ['PAL', 'Pail'],
    ['PCL', 'Parcel'],
    ['PCS', 'Pieces'],
    ['POV', 'Private Vehicle'],
    ['QTR', 'Quarters of Beef'],
    ['REL', 'Reel'],
    ['ROL', 'Roll'],
    ['SAK', 'Sack'],
    ['SHT', 'Sheet'],
    ['SID', 'Sides of Beef'],
    ['SKD', 'Skid'],
    ['TNK', 'Tank'],
    ['TIN', 'Tin'],
    ['TBN', 'Tote Bin'],
    ['TBE', 'Tube'],
    ['UNT', 'Unit'],
    ['VPK', 'Van Pack'],
    ['VEH', 'Vehicle'],
    ['WDC', 'Wooden Case'],
].map(([value, label]) => ({
    value: value,
    label: label,
    categoryLabel: 'CA Units - (ACE: CA to US)',
}));
export const RRF_PACKAGING_UNITS_OPTIONS_V1 = [
    ...RRF_PACKAGING_UNITS_ACI_US_TO_CA,
    ...RRF_PACKAGING_UNITS_ACE_CA_TO_US,
];
// V1-exact fresh-org defaults, sourced from `config/enterprise_features.php`
// (Dispatch) — the per-field `default` flags. On a fresh org with no admin
// override the routing row shows: Driver/Carrier, Truck, Trailer, Commodity,
// Quantity, Units, Packaging Units, Weight, Weight Units, Notes. Manifest,
// Percentage and Custom Field 1-5 default OFF (V1 `show_on_routing_row` = 0).
//
// V1 also defaults these checkboxes ON:
//   - `include_in_qb_description` = 1 for every row except Percentage
//   - `show_on_pwa` = 1 for Commodity, Quantity, Units, Notes
//   - `mandatory`   = 1 for Commodity, Quantity, Units, Packaging Units,
//                     Weight, Notes
// (Mandatory enforcement only fires when the module's `*_cfrw` enterprise
// feature is enabled — gated at the load create/update call sites.)
export const RRF_ROW_META = [
    { key: 'driver_carrier', label: 'Driver / Carrier', hasDefaultType: false, hasDefaultValue: false, hasCustomFieldName: false, hasMandatory: false, rowOverrides: { show_on_routing_row: true, include_in_qb_description: true } },
    { key: 'truck', label: 'Truck', hasDefaultType: false, hasDefaultValue: false, hasCustomFieldName: false, hasMandatory: false, rowOverrides: { show_on_routing_row: true, include_in_qb_description: true } },
    { key: 'trailer', label: 'Trailer', hasDefaultType: false, hasDefaultValue: false, hasCustomFieldName: false, hasMandatory: false, rowOverrides: { show_on_routing_row: true, include_in_qb_description: true } },
    // WAYP-946 Fix 2 — Commodity defaults its Default Type to 'Text' so the
    // V2 column doesn't render visually empty (it used to ship `''` which
    // collapses to `—` and read as a broken / unconfigured cell).
    { key: 'commodity', label: 'Commodity', hasDefaultType: true, hasDefaultValue: true, hasCustomFieldName: false, rowOverrides: { show_on_routing_row: true, show_on_pwa: true, mandatory: true, include_in_qb_description: true, default_type: 'Text' } },
    { key: 'quantity', label: 'Quantity', hasDefaultType: false, hasDefaultValue: true, hasCustomFieldName: false, blankFlags: ['settlement', 'show_on_invoice_grid', 'show_on_settlement_grid'], rowOverrides: { show_on_routing_row: true, show_on_pwa: true, mandatory: true, include_in_qb_description: true } },
    // Units + Packaging Units are locked-Dropdown rows whose Default Value
    // picks from the full V1 master enum (26 units / ~124 ACI+ACE packaging
    // codes). V1 stores the packaging EDI code (e.g. `PLL` → "Air Pallet").
    // V1 parity: the Default TYPE cell is blank for these (`type=blank`), so
    // `hasDefaultType=false` renders `—` (NOT a disabled "Dropdown"). The type
    // is still pinned to 'Dropdown' in the data via `lockedDefaultType` so the
    // Default Value renders as a select and downstream consumers resolve it.
    { key: 'units', label: 'Units', hasDefaultType: false, hasDefaultValue: true, hasCustomFieldName: false, lockedDefaultType: 'Dropdown', defaultValueOptions: RRF_UNITS_OPTIONS_V1, blankFlags: ['settlement', 'show_on_invoice_grid', 'show_on_settlement_grid'], rowOverrides: { show_on_routing_row: true, show_on_pwa: true, mandatory: true, include_in_qb_description: true, default_type: 'Dropdown', default_value: 'Pieces' } },
    { key: 'packaging_units', label: 'Packaging Units', hasDefaultType: false, hasDefaultValue: true, hasCustomFieldName: false, lockedDefaultType: 'Dropdown', defaultValueOptions: RRF_PACKAGING_UNITS_OPTIONS_V1, blankFlags: ['settlement', 'show_on_invoice_grid', 'show_on_settlement_grid'], rowOverrides: { show_on_routing_row: true, mandatory: true, include_in_qb_description: true, default_type: 'Dropdown', default_value: 'PLL' } },
    { key: 'weight', label: 'Weight', hasDefaultType: false, hasDefaultValue: true, hasCustomFieldName: false, blankFlags: ['show_on_invoice_grid', 'show_on_settlement_grid'], rowOverrides: { show_on_routing_row: true, mandatory: true, include_in_qb_description: true } },
    { key: 'weight_units', label: 'Weight Units', hasDefaultType: false, hasDefaultValue: true, hasCustomFieldName: false, blankFlags: ['show_on_invoice_grid', 'show_on_settlement_grid'], rowOverrides: { show_on_routing_row: true, include_in_qb_description: true } },
    { key: 'manifest', label: 'Manifest', hasDefaultType: false, hasDefaultValue: true, hasCustomFieldName: false, rowOverrides: { include_in_qb_description: true } },
    { key: 'custom_field_1', label: 'Custom Field 1', hasDefaultType: true, hasDefaultValue: true, hasCustomFieldName: true, rowOverrides: { include_in_qb_description: true } },
    { key: 'custom_field_2', label: 'Custom Field 2', hasDefaultType: true, hasDefaultValue: true, hasCustomFieldName: true, rowOverrides: { include_in_qb_description: true } },
    { key: 'custom_field_3', label: 'Custom Field 3', hasDefaultType: true, hasDefaultValue: true, hasCustomFieldName: true, rowOverrides: { include_in_qb_description: true } },
    { key: 'custom_field_4', label: 'Custom Field 4', hasDefaultType: true, hasDefaultValue: true, hasCustomFieldName: true, rowOverrides: { include_in_qb_description: true } },
    { key: 'custom_field_5', label: 'Custom Field 5', hasDefaultType: true, hasDefaultValue: true, hasCustomFieldName: true, rowOverrides: { include_in_qb_description: true } },
    { key: 'notes', label: 'Notes', hasDefaultType: false, hasDefaultValue: true, hasCustomFieldName: false, blankFlags: ['settlement', 'show_on_invoice_grid', 'show_on_settlement_grid'], rowOverrides: { show_on_routing_row: true, show_on_pwa: true, mandatory: true, include_in_qb_description: true } },
    { key: 'percentage', label: 'Percentage', hasDefaultType: false, hasDefaultValue: true, hasCustomFieldName: false, blankFlags: ['order_confirmation', 'bill_of_lading', 'driver_confirmation', 'carrier_confirmation', 'invoice', 'settlement', 'show_on_invoice_grid', 'show_on_settlement_grid'] },
];
export const RRF_ROW_KEYS = RRF_ROW_META.map((r) => r.key);
export const RRF_COLUMNS = [
    { key: 'show_on_routing_row', label: 'Show On Routing Row', type: 'checkbox' },
    // ATL-589 — labels say "Native App" (V2 has no PWA); the row keys are V1
    // config-shape parity and feed the native-app config payload — frozen.
    { key: 'show_on_pwa', label: 'Show on Native App', type: 'checkbox' },
    { key: 'editable_in_pwa', label: 'Editable in Native App', type: 'select_yes_no' },
    { key: 'mandatory', label: 'Mandatory', type: 'checkbox' },
    { key: 'include_in_qb_description', label: 'Include in Description QBO', type: 'checkbox' },
    { key: 'show_on_invoice_grid', label: 'Show On Invoice Grid', type: 'checkbox' },
    { key: 'show_on_settlement_grid', label: 'Show On Settlement Grid', type: 'checkbox' },
    { key: 'order_confirmation', label: 'Order Confirmation', type: 'checkbox' },
    { key: 'bill_of_lading', label: 'Bill of Lading', type: 'checkbox' },
    { key: 'driver_confirmation', label: 'Driver Confirmation', type: 'checkbox' },
    { key: 'carrier_confirmation', label: 'Carrier Confirmation', type: 'checkbox' },
    { key: 'invoice', label: 'Invoice', type: 'checkbox' },
    { key: 'settlement', label: 'Settlement', type: 'checkbox' },
    { key: 'sms', label: 'SMS', type: 'checkbox' },
];
/** Build the empty per-row defaults — every checkbox false, every select empty. */
export function makeDefaultRowState(rowKey) {
    const meta = RRF_ROW_META.find((r) => r.key === rowKey);
    const base = {
        show_on_routing_row: false,
        show_on_pwa: false,
        editable_in_pwa: '',
        mandatory: false,
        include_in_qb_description: false,
        show_on_invoice_grid: false,
        show_on_settlement_grid: false,
        order_confirmation: false,
        bill_of_lading: false,
        driver_confirmation: false,
        carrier_confirmation: false,
        invoice: false,
        settlement: false,
        sms: false,
        default_type: '',
        default_value: '',
    };
    if (meta?.hasCustomFieldName) {
        const n = meta.key.split('_').pop(); // "1" .. "5"
        Object.assign(base, {
            [`custom_field_${n}_name`]: '',
            [`custom_field_${n}_default_type`]: '',
            [`custom_field_${n}_default_value`]: '',
            [`custom_field_${n}_default_type_options`]: '',
        });
    }
    if (meta?.rowOverrides) {
        Object.assign(base, meta.rowOverrides);
    }
    return base;
}
/** Build the full default configJson skeleton for a brand-new module install. */
export function makeDefaultRrfConfigJson() {
    const rows = {};
    for (const meta of RRF_ROW_META) {
        rows[meta.key] = makeDefaultRowState(meta.key);
    }
    return {
        routing_rows_sorting_order: [...RRF_ROW_KEYS],
        rows,
    };
}
/**
 * Merge a stored partial configJson over the defaults, returning a complete
 * `RrfConfigJson`. Tolerant of:
 *   - missing `routing_rows_sorting_order` (falls back to default order)
 *   - missing `rows.<key>` (filled with defaults)
 *   - extra keys (preserved untouched — forward-compat)
 */
export function mergeRrfConfigJson(stored) {
    const defaults = makeDefaultRrfConfigJson();
    if (!stored || typeof stored !== 'object')
        return defaults;
    const rawOrder = stored.routing_rows_sorting_order;
    const order = Array.isArray(rawOrder) && rawOrder.every((s) => typeof s === 'string') && rawOrder.length > 0
        ? // Filter to known keys + append any missing-from-stored at the end so
            // legacy installs upgraded to a new row don't drop the new row.
            [
                ...rawOrder.filter((k) => RRF_ROW_KEYS.includes(k)),
                ...RRF_ROW_KEYS.filter((k) => !rawOrder.includes(k)),
            ]
        : defaults.routing_rows_sorting_order;
    const rawRows = stored.rows ?? {};
    const rows = {};
    for (const meta of RRF_ROW_META) {
        const merged = { ...defaults.rows[meta.key], ...(rawRows[meta.key] ?? {}) };
        // WAYP-946 — backward-compat: legacy orgs persisted `default_type: ''` /
        // `default_value: ''` for rows the customer never configured. The new
        // V1-parity defaults (Commodity → 'Text', Units → 'Dropdown'/'Pieces',
        // Packaging Units → 'Dropdown'/'Air Pallet') should still apply for
        // those legacy rows so the cell doesn't render visually empty. When
        // the stored field is the empty-string sentinel AND the row meta
        // supplies a default in `rowOverrides`, fall back to the override.
        const overrides = meta.rowOverrides;
        if (overrides) {
            if (overrides.default_type !== undefined && !merged.default_type) {
                merged.default_type = overrides.default_type;
            }
            if (overrides.default_value !== undefined && !merged.default_value) {
                merged.default_value = overrides.default_value;
            }
        }
        // Locked-type rows (Units / Packaging Units) MUST honour the lock even
        // if a legacy stored payload had something else. The Default Type
        // select renders disabled, so the stored value cannot be edited away
        // from the lock — pin the in-memory state to the lock so the read
        // path matches the write path.
        if (meta.lockedDefaultType && merged.default_type !== meta.lockedDefaultType) {
            merged.default_type = meta.lockedDefaultType;
        }
        // Backward-compat: legacy installs stored the human LABEL as the Default
        // Value (e.g. Packaging Units "Air Pallet") before the dropdown switched
        // to V1 EDI codes (value `PLL`, label "Air Pallet"). If the stored value
        // isn't a valid option value but matches an option label, normalise it to
        // the code so the select still resolves it (self-heals on next save).
        if (meta.defaultValueOptions && merged.default_value) {
            const opts = meta.defaultValueOptions;
            const isValidValue = opts.some((o) => o.value === merged.default_value);
            if (!isValidValue) {
                const byLabel = opts.find((o) => o.label === merged.default_value);
                if (byLabel)
                    merged.default_value = byLabel.value;
            }
        }
        // V1 parity — the assignment rows (Driver/Carrier, Truck, Trailer) are not
        // mandatory-capable. Self-heal any legacy stored `mandatory: true` so the
        // load-form header never shows a red asterisk / mandatory border for them
        // and the pre-write validator never enforces them.
        if (meta.hasMandatory === false && merged.mandatory) {
            merged.mandatory = false;
        }
        rows[meta.key] = merged;
    }
    return { routing_rows_sorting_order: order, rows };
}
//# sourceMappingURL=routing-row-fields.js.map